import React from 'react';
import { BOTTOM_INDENTS, COLORS } from '@constants';
import { Typography } from '@ui';
import { formatPrice, formatString } from '@utils';
import { Col, Flex, Row } from 'antd';
import { getCommissionData } from '../../utils/getCommissionData';
import { getSubscriptionOrderLabel } from '../../utils/getSubscriptionOrderLabel';
import { renderAddress } from '../../utils/renderAddress';
import { StyledPriceSection, StyledRow, StyledSubscription } from './styled';

const PRICE_LINES = [
    { label: 'Undiscounted Sub-total', field: 'subtotalPrice', borderColor: 'transparent' },
    { label: 'Discount', field: 'ecommerceTotalDiscountsPrice', isNegative: true },
    { label: 'Subtotal', field: 'discountedSubtotalPrice' },
    { label: 'Shipping', field: 'shippingPrice' },
    { label: 'Taxes', field: 'taxPrice' },
    { label: 'Total Price', field: 'totalPrice', type: 'badgeSmall', borderColor: COLORS.$gray100Black }
];

const PriceLine = ({ label, value, borderColor, type }) => {
    return (
        <StyledRow $borderColor={borderColor} gap={20} justify='space-between'>
            <Typography.Text data-e2e={`orders_${formatString(label)}_row`} type={type}>
                {label}
            </Typography.Text>
            <Typography.Text data-e2e={`orders_${formatString(label)}_value`} type={type}>
                {value}
            </Typography.Text>
        </StyledRow>
    );
};

const ColumnDetails = ({ label, value }) => {
    return (
        <Col span={6}>
            <Flex vertical gap={4}>
                <Typography.Text
                    data-e2e={`orders_${formatString(label)}_column`}
                    color={COLORS.$gray40Black}
                    type='descriptor2'
                >
                    {label}
                </Typography.Text>
                <Typography.Text data-e2e={`orders_${formatString(label)}_value`} type='body2'>
                    {value}
                </Typography.Text>
            </Flex>
        </Col>
    );
};

const OrderDetailsFooter = ({ record, summary = null }) => {
    const commissionData = getCommissionData(
        record.metrics.commissionTypeName,
        record.orderItems,
        record.metrics.isCommissionLimitedToSubtotal
    );

    return (
        <Flex vertical>
            <Flex style={BOTTOM_INDENTS.S} align='flex-start'>
                {summary}
                <StyledPriceSection data-e2e='orders_price_block' vertical>
                    {PRICE_LINES.map(
                        ({ label, field, isNegative, type = 'body2', borderColor = COLORS.$gray15Black }) => (
                            <PriceLine
                                key={label}
                                type={type}
                                borderColor={borderColor}
                                label={label}
                                value={`${isNegative ? '-' : ''}${formatPrice(record[field])}`}
                            />
                        )
                    )}
                </StyledPriceSection>
            </Flex>
            <StyledRow data-e2e='orders_billing-details_block' $borderColor={COLORS.$gray15Black} $padding='32px 16px'>
                <Row>
                    <Col span={6}>
                        <Flex vertical>
                            <Typography.Text
                                data-e2e='orders_billing-address_column'
                                color={COLORS.$gray40Black}
                                type='descriptor2'
                            >
                                Billing Address
                            </Typography.Text>
                            {renderAddress(record.billingAddress, 'billing')}
                        </Flex>
                    </Col>
                    <Col span={6}>
                        <Flex vertical>
                            <Typography.Text
                                data-e2e='orders_shipping-address_column'
                                color={COLORS.$gray40Black}
                                type='descriptor2'
                            >
                                Shipping Address
                            </Typography.Text>
                            {renderAddress(record.shippingAddress, 'shipping')}
                        </Flex>
                    </Col>
                    <Col span={6}>
                        <Flex vertical gap={4}>
                            <Typography.Text
                                data-e2e='orders_customer-email_column'
                                color={COLORS.$gray40Black}
                                type='descriptor2'
                            >
                                Customer Email
                            </Typography.Text>
                            <Typography.Text data-e2e='orders_customer-email_value' type='descriptor1'>
                                {record.email ? (
                                    <a
                                        className='tets'
                                        href={`mailto:${record.email}`}
                                        target='_blank'
                                        rel='noreferrer'
                                    >
                                        {record.email}
                                    </a>
                                ) : (
                                    '-'
                                )}
                            </Typography.Text>
                        </Flex>
                    </Col>
                    <Col span={6}>
                        <Flex vertical gap={4}>
                            <Typography.Text
                                data-e2e='orders_phone_column'
                                color={COLORS.$gray40Black}
                                type='descriptor2'
                            >
                                Phone
                            </Typography.Text>
                            <Typography.Text data-e2e='orders_phone_value' type='descriptor1'>
                                {record.phone || '-'}
                            </Typography.Text>
                        </Flex>
                    </Col>
                </Row>
            </StyledRow>
            <StyledRow
                data-e2e='orders_engagement-details_block'
                $borderColor={COLORS.$gray15Black}
                $padding='32px 16px'
            >
                <Row>
                    <ColumnDetails label='Total engagements (last 90 days)' value={record.metrics.totalEngagements} />
                    <ColumnDetails label='Days to conversion' value={record.metrics.daysToConversionToDisplay} />
                    <ColumnDetails label='Last engagement' value={record.metrics.lastEngagement} />
                    <ColumnDetails label='Commission type' value={commissionData.type} />
                </Row>
            </StyledRow>
            {record.subscriptionOrderPosition ? (
                <StyledSubscription data-e2e='orders_subscription-order_block' justify='space-between' align='center'>
                    <Flex align='flex-end' gap={6}>
                        <Typography.Text
                            data-e2e='orders_subscription-order_title'
                            color={COLORS.$alert}
                            type='badgeMedium'
                        >
                            Subscription order
                        </Typography.Text>
                        <Typography.Text
                            data-e2e='orders_subscription-order_value'
                            color={COLORS.$alert}
                            type='badgeLarge'
                        >
                            {getSubscriptionOrderLabel(record.subscriptionOrderPosition)}
                        </Typography.Text>
                    </Flex>
                    <Typography.Text
                        data-e2e='orders_subscription-order_description'
                        color={COLORS.$alert}
                        type='badgeSmall'
                    >
                        Klickly bills only for 1st and 2nd subscription orders per customer.
                    </Typography.Text>
                </StyledSubscription>
            ) : null}
        </Flex>
    );
};

export default OrderDetailsFooter;
